import { ReactNode } from 'react'
import { useOutletContext } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import BaseSidepanel, { type Option } from 'modules/core/components/Sidepanel'
import i18n from 'modules/core/lib/i18n'

import { RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import { canRestartExpensable } from 'modules/expenses/lib/expensables/availableActions'
import { Expensable } from 'modules/expenses/types/Expensable'

import NavigationHeader from './_NavigationHeader'
import { useBulkActions } from './hooks/useBulkActions'

interface SidepanelProps {
  expensable?: Expensable
  open: boolean
  children: ReactNode
  onClose: () => void
  onCloseClicked?: () => void
  canRestartApprovalFlow: boolean
  showTitle?: boolean
}

const useOptions = (expensable: Expensable | undefined, canRestartApprovalFlow: boolean) => {
  const { section } = useOutletContext<RouteContextParams>()
  const bulkActions = useBulkActions()
  const options: Option[] = []

  if (!expensable) return undefined

  if (canRestartExpensable(expensable, canRestartApprovalFlow, section)) {
    options.push({
      title: i18n.t('expenses_v2.list.actions.reset_approval_flow'),
      onClick: bulkActions.restartApprovalFlow,
    })
  }

  return options.length > 0 ? options : undefined
}

const Sidepanel = ({
  expensable,
  open,
  children,
  onClose,
  onCloseClicked,
  canRestartApprovalFlow,
  showTitle = true,
}: SidepanelProps) => {
  const { dataCollectionEnabled } = useOutletContext<RouteContextParams>()
  const options = useOptions(expensable, canRestartApprovalFlow)

  const renderTitle = () => {
    if (!showTitle) return undefined

    // When creating a new expense
    if (!expensable) {
      return (
        <Text.Normal weight="semibold">
          {i18n.t('expenses_v2.navigator.detail_title_new')}
        </Text.Normal>
      )
    }

    const hasReimbursableAmount =
      expensable.reimbursableAmount !== undefined && expensable.reimbursableAmount !== null
    const totalReimbursementLabel = i18n.t(
      'expenses_v2.confirmation_screen.expense.payment_block.total_reimbursement'
    )
    const totalReimbursementValue = hasReimbursableAmount
      ? `${i18n.formatNumber(
          Number(expensable.reimbursableAmount) / 100,
          'currency',
          expensable.reimbursableCurrency ?? 'EUR'
        )} ${totalReimbursementLabel}`
      : totalReimbursementLabel

    const totalReimbursementRow = (
      <Text.Normal size="200" weight="medium" color="grey800">
        {totalReimbursementValue}
      </Text.Normal>
    )

    const titleContent = dataCollectionEnabled ? (
      <Box flexDirection="column" gap="s4" alignItems="center">
        <Box paddingX="s24">
          <Text.Normal weight="semibold">
            {i18n.t('expenses_v2.navigator.detail_title')}
          </Text.Normal>
        </Box>
        {totalReimbursementRow}
      </Box>
    ) : (
      <Box flexDirection="column" gap="s4" alignItems="center">
        <NavigationHeader expensable={expensable} />
        {totalReimbursementRow}
      </Box>
    )

    return titleContent
  }

  return (
    <BaseSidepanel
      title={renderTitle()}
      options={options}
      open={open}
      width="wide"
      boxPadding={false}
      closeLabel={i18n.t('expenses_v2.modals.detail.actions.close_detail')}
      onClose={onClose}
      onCloseClicked={onCloseClicked}
      allowBackgroundInteraction
    >
      {children}
    </BaseSidepanel>
  )
}

export default observer(Sidepanel)
